import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Handshake, MapPin, Plus, Send, Trash2, Car, Wrench, CheckCircle2, Phone, CalendarDays, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { brl, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const UFS = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];
const STATUS_LABEL = { aberta: "Aberto", preenchida: "Preenchido", cancelada: "Cancelado" };
const STATUS_COLOR = {
  aberta: "bg-green-100 text-green-700 border-green-200",
  preenchida: "bg-sky-100 text-sky-700 border-sky-200",
  cancelada: "bg-slate-100 text-slate-600 border-slate-200",
};
const RESP_LABEL = { enviada: "Candidatura enviada", aceita: "Você foi selecionado", recusada: "Não selecionado" };
const toggleList = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

export default function Network() {
  const [meta, setMeta] = useState({ specialties: [] });
  const [profile, setProfile] = useState(null);
  const [myRequests, setMyRequests] = useState([]);
  const [opps, setOpps] = useState([]);
  const [myResponses, setMyResponses] = useState([]);
  const [allOpen, setAllOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [reqForm, setReqForm] = useState({ title: "", description: "", city: "", state: "", date: "", duration: "", offered_value: "" });
  const [respOpen, setRespOpen] = useState(null);
  const [respMsg, setRespMsg] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);
  const [sending, setSending] = useState(false);
  const [pform, setPform] = useState({ specialties: [], cities: [], radius_km: 50, day_rate: "", has_vehicle: false, has_tools: false, notes: "", active: true });

  const load = () => {
    api.get("/network/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/network/profile/me").then((r) => setProfile(r.data)).catch(() => {});
    api.get("/network/requests/mine").then((r) => setMyRequests(r.data)).catch(() => {});
    api.get("/network/responses/mine").then((r) => setMyResponses(r.data)).catch(() => {});
  };
  const loadOpps = () => {
    api.get("/network/opportunities", { params: { all_open: allOpen } }).then((r) => setOpps(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { loadOpps(); }, [allOpen]);

  useEffect(() => {
    if (profile?.exists) {
      setPform({
        specialties: profile.specialties || [], cities: profile.cities || [],
        radius_km: profile.radius_km ?? 50, day_rate: profile.day_rate ?? "",
        has_vehicle: !!profile.has_vehicle, has_tools: !!profile.has_tools,
        notes: profile.notes || "", active: profile.active !== false,
      });
    }
  }, [profile]);

  const specName = (id) => meta.specialties.find((s) => s.id === id)?.name || id;

  const createRequest = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/network/requests", { ...reqForm, offered_value: Number(reqForm.offered_value) || 0 });
      toast.success(data.message);
      setShowForm(false);
      setReqForm({ title: "", description: "", city: "", state: "", date: "", duration: "", offered_value: "" });
      load();
      loadOpps();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  const respond = async (rid) => {
    setSending(true);
    try {
      const { data } = await api.post(`/network/requests/${rid}/respond`, { message: respMsg });
      toast.success(data.message);
      setRespOpen(null);
      setRespMsg("");
      load();
      loadOpps();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  const viewDetail = async (r) => {
    if (expanded === r.id) { setExpanded(null); setDetail(null); return; }
    try {
      const { data } = await api.get(`/network/requests/${r.id}`);
      setDetail(data);
      setExpanded(r.id);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const acceptResp = async (resp) => {
    try {
      const { data } = await api.put(`/network/responses/${resp.id}/accept`);
      toast.success(data.message);
      if (data.professional_contact?.whatsapp || data.professional_contact?.phone) {
        toast.info(`Contato de ${data.professional_contact.name || "profissional"}: ${data.professional_contact.whatsapp || data.professional_contact.phone}`, { duration: 15000 });
      }
      load();
      viewDetail({ id: expanded });
      setExpanded(null);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const cancelRequest = async (r) => {
    if (!window.confirm("Cancelar este pedido de apoio?")) return;
    try {
      await api.put(`/network/requests/${r.id}/cancel`);
      toast.success("Pedido cancelado.");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/network/profile", { ...pform, day_rate: Number(pform.day_rate) || 0, radius_km: Number(pform.radius_km) || 50 });
      toast.success(data.message);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-5 animate-in-up max-w-3xl" data-testid="network-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Handshake className="h-6 w-6 text-sky-600" /> GEPR Network <Badge className="bg-amber-100 text-amber-700 border-amber-200">BETA</Badge>
        </h1>
        <p className="text-sm text-slate-500">Apoio presencial entre profissionais — cubra regiões, divida demandas, encontre reforço</p>
      </div>

      <Tabs defaultValue="oportunidades">
        <TabsList className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="oportunidades" data-testid="net-tab-opps">Oportunidades ({opps.length})</TabsTrigger>
          <TabsTrigger value="pedidos" data-testid="net-tab-mine">Meus pedidos ({myRequests.length})</TabsTrigger>
          <TabsTrigger value="candidaturas" data-testid="net-tab-responses">Candidaturas ({myResponses.length})</TabsTrigger>
          <TabsTrigger value="perfil" data-testid="net-tab-profile">Perfil de apoio</TabsTrigger>
        </TabsList>

        <TabsContent value="oportunidades" className="mt-4 space-y-3">
          <div className="flex items-center justify-between border border-slate-200 rounded-lg p-3">
            <p className="text-sm text-slate-600">{allOpen ? "Mostrando todos os pedidos abertos do Brasil" : "Mostrando pedidos na sua região de atuação"}</p>
            <Switch checked={allOpen} onCheckedChange={setAllOpen} data-testid="opps-all-toggle" />
          </div>
          {opps.length === 0 && (
            <Card className="border-slate-200"><CardContent className="p-6 text-center text-sm text-slate-500" data-testid="no-opps">
              Nenhum pedido de apoio aberto na sua região. Ative seu perfil na aba <b>Perfil de apoio</b> para receber oportunidades.
            </CardContent></Card>
          )}
          {opps.map((o) => (
            <Card key={o.id} className="border-slate-200" data-testid={`opp-card-${o.id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-sm">{o.title} {o.na_sua_cidade && <Badge className="ml-1 bg-green-100 text-green-700 border-green-200 text-[10px]">na sua cidade</Badge>}</p>
                    <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                      <MapPin className="h-3 w-3" /> {o.city}/{o.state} · {o.requester_name?.split(" ")[0]}
                      {o.date && <span className="flex items-center gap-1"><CalendarDays className="h-3 w-3 ml-1" /> {o.date}</span>}
                      {o.duration && ` · ${o.duration}`}
                    </p>
                  </div>
                  <p className="font-mono-num font-semibold text-sky-700 shrink-0">{o.offered_value > 0 ? brl(o.offered_value) : "A combinar"}</p>
                </div>
                <p className="text-sm text-slate-600">{o.description}</p>
                {o.my_response ? (
                  <p className="text-xs text-sky-700 bg-sky-50 border border-sky-200 rounded-lg p-2">{RESP_LABEL[o.my_response]}</p>
                ) : respOpen === o.id ? (
                  <div className="border border-sky-200 bg-sky-50 rounded-lg p-3 space-y-2">
                    <Textarea rows={2} placeholder="Apresente-se: experiência, disponibilidade, equipamentos..." data-testid={`opp-msg-${o.id}`} value={respMsg} onChange={(e) => setRespMsg(e.target.value)} maxLength={500} />
                    <div className="flex gap-2">
                      <Button size="sm" className="h-10 bg-sky-600 hover:bg-sky-700 text-white" disabled={sending} onClick={() => respond(o.id)} data-testid={`opp-send-${o.id}`}>
                        <Send className="h-4 w-4 mr-1" /> Enviar candidatura
                      </Button>
                      <Button size="sm" variant="ghost" className="h-10" onClick={() => setRespOpen(null)}>Cancelar</Button>
                    </div>
                  </div>
                ) : (
                  <Button size="sm" variant="outline" className="h-10 border-sky-300 text-sky-700 hover:bg-sky-50" onClick={() => setRespOpen(o.id)} data-testid={`opp-respond-${o.id}`}>
                    Candidatar-se
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="pedidos" className="mt-4 space-y-3">
          {!showForm ? (
            <Button className="w-full h-12 bg-sky-600 hover:bg-sky-700 text-white" onClick={() => setShowForm(true)} data-testid="nr-new-btn">
              <Plus className="h-4 w-4 mr-2" /> Pedir apoio presencial
            </Button>
          ) : (
            <Card className="border-sky-300 bg-sky-50" data-testid="nr-form-card">
              <CardHeader><CardTitle className="text-base">Novo pedido de apoio presencial</CardTitle></CardHeader>
              <CardContent>
                <form onSubmit={createRequest} className="space-y-3">
                  <div className="space-y-1.5">
                    <Label>Título *</Label>
                    <Input className="h-12 bg-white" data-testid="nr-title" required value={reqForm.title} onChange={(e) => setReqForm({ ...reqForm, title: e.target.value })} placeholder="Ex: Preciso de reforço para instalação de 3 splits" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Descreva o serviço e o apoio necessário *</Label>
                    <Textarea rows={3} className="bg-white" data-testid="nr-description" required value={reqForm.description} onChange={(e) => setReqForm({ ...reqForm, description: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-2 space-y-1.5">
                      <Label>Cidade *</Label>
                      <Input className="h-12 bg-white" data-testid="nr-city" required value={reqForm.city} onChange={(e) => setReqForm({ ...reqForm, city: e.target.value })} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>UF *</Label>
                      <Select value={reqForm.state} onValueChange={(v) => setReqForm({ ...reqForm, state: v })} required>
                        <SelectTrigger className="h-12 bg-white" data-testid="nr-state"><SelectValue placeholder="UF" /></SelectTrigger>
                        <SelectContent>{UFS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                      <Label>Data prevista</Label>
                      <Input className="h-12 bg-white" type="date" data-testid="nr-date" value={reqForm.date} onChange={(e) => setReqForm({ ...reqForm, date: e.target.value })} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Duração</Label>
                      <Input className="h-12 bg-white" data-testid="nr-duration" value={reqForm.duration} onChange={(e) => setReqForm({ ...reqForm, duration: e.target.value })} placeholder="Ex: 1 dia" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Valor oferecido (R$)</Label>
                      <Input className="h-12 bg-white font-mono-num" type="number" step="0.01" min="0" data-testid="nr-value" value={reqForm.offered_value} onChange={(e) => setReqForm({ ...reqForm, offered_value: e.target.value })} placeholder="0 = a combinar" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" className="flex-1 h-12 bg-sky-600 hover:bg-sky-700 text-white" disabled={sending} data-testid="nr-submit">{sending ? "Publicando..." : "Publicar pedido"}</Button>
                    <Button type="button" variant="outline" className="h-12" onClick={() => setShowForm(false)} data-testid="nr-cancel">Cancelar</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {myRequests.length === 0 && !showForm && <p className="text-sm text-slate-500">Você ainda não publicou pedidos de apoio.</p>}
          {myRequests.map((r) => (
            <Card key={r.id} className="border-slate-200" data-testid={`netreq-card-${r.id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-sm">{r.title}</p>
                    <p className="text-xs text-slate-500">{r.city}/{r.state} · {fmtDate(r.created_at)}{r.date ? ` · previsto: ${r.date}` : ""} · {r.offered_value > 0 ? brl(r.offered_value) : "a combinar"}</p>
                  </div>
                  <Badge variant="outline" className={`text-[10px] shrink-0 ${STATUS_COLOR[r.status]}`}>{STATUS_LABEL[r.status]}</Badge>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline" className="h-10" onClick={() => viewDetail(r)} data-testid={`netreq-view-${r.id}`}>
                    {expanded === r.id ? "Ocultar" : `Candidatos (${r.responses_count})`}
                  </Button>
                  {r.status === "aberta" && (
                    <Button size="sm" variant="ghost" className="h-10 text-red-600 hover:bg-red-50" onClick={() => cancelRequest(r)} data-testid={`netreq-cancel-${r.id}`}>Cancelar</Button>
                  )}
                </div>
                {expanded === r.id && detail && (
                  <div className="border-t border-slate-100 pt-3 space-y-2" data-testid="netreq-detail">
                    {(detail.responses || []).length === 0 && <p className="text-xs text-slate-500">Nenhuma candidatura ainda. Profissionais da região verão seu pedido.</p>}
                    {(detail.responses || []).map((resp) => (
                      <div key={resp.id} className="border border-slate-200 rounded-lg p-3 space-y-1.5" data-testid={`resp-card-${resp.id}`}>
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium">{resp.professional_name}</p>
                          <Badge variant="outline" className={`text-[10px] ${resp.status === "aceita" ? "bg-green-100 text-green-700 border-green-200" : resp.status === "recusada" ? "bg-slate-100 text-slate-500" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                            {resp.status === "aceita" ? "Aceito" : resp.status === "recusada" ? "Não selecionado" : "Aguardando"}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-500">
                          {(resp.cities || []).map((c) => `${c.city}/${c.state}`).join(", ")} · diária {resp.day_rate > 0 ? brl(resp.day_rate) : "a combinar"}
                        </p>
                        {resp.message && <p className="text-sm text-slate-600">{resp.message}</p>}
                        {detail.status === "aberta" && resp.status === "enviada" && (
                          <Button size="sm" className="h-10 bg-green-600 hover:bg-green-700 text-white" onClick={() => acceptResp(resp)} data-testid={`resp-accept-${resp.id}`}>
                            <CheckCircle2 className="h-4 w-4 mr-1" /> Aceitar e liberar contato
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="candidaturas" className="mt-4 space-y-2">
          {myResponses.length === 0 && <p className="text-sm text-slate-500">Você ainda não se candidatou a pedidos de apoio.</p>}
          {myResponses.map((c) => (
            <Card key={c.id} className="border-slate-200" data-testid={`cand-card-${c.id}`}>
              <CardContent className="p-4 space-y-1.5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-sm">{c.request_title}</p>
                    <p className="text-xs text-slate-500">{c.city}/{c.state}{c.date ? ` · ${c.date}` : ""} · {c.offered_value > 0 ? brl(c.offered_value) : "a combinar"}</p>
                  </div>
                  <Badge variant="outline" className={`text-[10px] shrink-0 ${c.status === "aceita" ? "bg-green-100 text-green-700 border-green-200" : c.status === "recusada" ? "bg-slate-100 text-slate-500" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                    {c.status === "aceita" ? "Selecionado" : c.status === "recusada" ? "Não selecionado" : "Aguardando"}
                  </Badge>
                </div>
                {c.status === "aceita" && c.contact && (
                  <p className="text-xs bg-green-50 border border-green-200 rounded-lg p-2.5 text-green-800 flex items-center gap-1.5" data-testid={`contact-${c.id}`}>
                    <Phone className="h-3.5 w-3.5 shrink-0" /> Contato do solicitante: <b>{c.contact.name || "-"}</b> · {c.contact.whatsapp || c.contact.phone || "não informado"}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="perfil" className="mt-4">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-base">{profile?.exists ? "Meu perfil de apoio presencial" : "Ofereça apoio presencial na sua região"}</CardTitle>
              <p className="text-xs text-slate-500">Disponibilize-se para reforçar colegas em serviços presenciais — e encontre reforço quando precisar.</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={saveProfile} className="space-y-4">
                {profile?.exists && (
                  <div className="flex items-center gap-4 border border-slate-200 rounded-lg p-3 text-sm" data-testid="my-network-stats">
                    <span><b className="font-mono-num">{profile.jobs_done || 0}</b> <span className="text-slate-500">trabalhos aceitos</span></span>
                  </div>
                )}
                <div className="flex items-center justify-between border border-slate-200 rounded-lg p-3">
                  <div>
                    <p className="font-medium text-sm">Disponível para apoio presencial</p>
                    <p className="text-xs text-slate-500">Desative para pausar candidaturas e oportunidades</p>
                  </div>
                  <Switch checked={pform.active} onCheckedChange={(v) => setPform({ ...pform, active: v })} data-testid="np-active" />
                </div>
                <div className="space-y-1.5">
                  <Label>Especialidades *</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {meta.specialties.map((s) => (
                      <label key={s.id} className="flex items-center gap-2 border border-slate-200 rounded-lg p-2.5 cursor-pointer text-sm" data-testid={`np-spec-${s.id}`}>
                        <Checkbox checked={pform.specialties.includes(s.id)} onCheckedChange={() => setPform({ ...pform, specialties: toggleList(pform.specialties, s.id) })} />
                        {s.name}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Cidades de atuação * (até 10)</Label>
                  {pform.cities.map((c, i) => (
                    <div key={i} className="flex gap-2">
                      <Input className="h-11 flex-1" placeholder="Cidade" data-testid={`np-city-${i}`} value={c.city} onChange={(e) => setPform({ ...pform, cities: pform.cities.map((x, xi) => xi === i ? { ...x, city: e.target.value } : x) })} />
                      <Select value={c.state} onValueChange={(v) => setPform({ ...pform, cities: pform.cities.map((x, xi) => xi === i ? { ...x, state: v } : x) })}>
                        <SelectTrigger className="h-11 w-24" data-testid={`np-state-${i}`}><SelectValue placeholder="UF" /></SelectTrigger>
                        <SelectContent>{UFS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button type="button" variant="ghost" size="icon" className="h-11 w-11 text-red-500" onClick={() => setPform({ ...pform, cities: pform.cities.filter((_, xi) => xi !== i) })} data-testid={`np-city-del-${i}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  {pform.cities.length < 10 && (
                    <Button type="button" variant="outline" className="h-10 w-full" onClick={() => setPform({ ...pform, cities: [...pform.cities, { city: "", state: "" }] })} data-testid="np-city-add">
                      <Plus className="h-4 w-4 mr-1" /> Adicionar cidade
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Raio de atuação (km)</Label>
                    <Input className="h-12 font-mono-num" type="number" min="5" data-testid="np-radius" value={pform.radius_km} onChange={(e) => setPform({ ...pform, radius_km: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Diária (R$)</Label>
                    <Input className="h-12 font-mono-num" type="number" step="0.01" min="0" data-testid="np-dayrate" value={pform.day_rate} onChange={(e) => setPform({ ...pform, day_rate: e.target.value })} placeholder="0 = a combinar" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <label className="flex items-center gap-2 border border-slate-200 rounded-lg p-2.5 cursor-pointer text-sm" data-testid="np-vehicle">
                    <Checkbox checked={pform.has_vehicle} onCheckedChange={(v) => setPform({ ...pform, has_vehicle: !!v })} />
                    <Car className="h-4 w-4 text-slate-500" /> Veículo próprio
                  </label>
                  <label className="flex items-center gap-2 border border-slate-200 rounded-lg p-2.5 cursor-pointer text-sm" data-testid="np-tools">
                    <Checkbox checked={pform.has_tools} onCheckedChange={(v) => setPform({ ...pform, has_tools: !!v })} />
                    <Wrench className="h-4 w-4 text-slate-500" /> Ferramentas completas
                  </label>
                </div>
                <div className="space-y-1.5">
                  <Label>Observações (disponibilidade, experiência)</Label>
                  <Textarea rows={2} data-testid="np-notes" value={pform.notes} onChange={(e) => setPform({ ...pform, notes: e.target.value })} maxLength={500} />
                </div>
                <Button type="submit" className="w-full h-12 bg-sky-600 hover:bg-sky-700 text-white" disabled={sending} data-testid="np-save">
                  {sending ? "Salvando..." : "Salvar perfil de apoio"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
